// src/components/toast/ToastProvider.jsx
// @ts-nocheck
import React, { createContext, useContext, useState, useCallback, useEffect } from "react"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import PropTypes from "prop-types"

const ToastContext = createContext(null)

/**
 * hook to access toast
 */
export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error("useToast must be used within ToastProvider")
  return ctx
}

/**
 * ToastProvider wraps app/area where toasts are needed.
 * Provides showToast({ message, type, duration })
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const showToast = useCallback(
    (message, type = "info", duration = 4000) => {
      const id = crypto.randomUUID()
      setToasts(t => [...t, { id, message, type }])
      setTimeout(() => {
        setToasts(t => t.filter(tt => tt.id !== id))
      }, duration)
    },
    []
  )

  const contextValue = {
    showToast
  }

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-3">
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => setToasts(ts => ts.filter(x => x.id !== t.id))} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

ToastProvider.propTypes = {
  children: PropTypes.node
}

/**
 * Individual toast card with Lottie animation
 */
function Toast({ message, type, onClose }) {
  // map type to lottie file (adjust paths if you store differently)
  const mapping = {
    success: "/animations/success.lottie",
    error: "/animations/error.lottie",
    info: "/animations/info.lottie"
  }
  const src = mapping[type] || mapping.info

  // auto-dismiss small fade animation could be added; keeping simple
  return (
    <div
      className={`
        flex items-center space-x-3 w-full max-w-sm rounded shadow-lg px-4 py-3
        ${type === "success" ? "bg-green-50 border border-green-300" : ""}
        ${type === "error" ? "bg-red-50 border border-red-300" : ""}
        ${type === "info" ? "bg-blue-50 border border-blue-300" : ""}
      `}
      role="alert"
    >
      <div className="flex-shrink-0 w-10 h-10">
        <DotLottieReact
          src={src}
          loop
          autoplay
          style={{ width: 40, height: 40 }}
        />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium">
          {type === "success" && "Success:"}
          {type === "error" && "Error:"}
          {type === "info" && "Info:"}{" "}
          <span className="ml-1">{message}</span>
        </p>
      </div>
      <button
        onClick={onClose}
        className="text-gray-500 hover:text-gray-700 font-bold"
        aria-label="close"
      >
        ×
      </button>
    </div>
  )
}

Toast.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["success", "error", "info"]),
  onClose: PropTypes.func.isRequired
}
